import { ApiError, ErrorCode } from '@approvalmax/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import { QueryKeys } from 'modules/common';
import { backend } from 'modules/data';
import { clearCacheStorage } from 'modules/data-providers';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { api } from 'services/api';
import { notificationService } from 'services/notification';

import { useXeroAirwallexBatchPaymentFormContext } from '../../../../XeroAirwallexBatchPaymentForm.context';
import {
    accountCurrencyKey,
    bankCountryCodeKey,
    entityTypeKey,
    keysGeneral,
    keysPaymentMethod,
    localClearingSystemKey,
    paymentMethodKey,
} from './AddBeneficiaryPopup.constants';
import { tuneAirwallexFieldsAnswer } from './AddBeneficiaryPopup.helpers';
import { messages } from './AddBeneficiaryPopup.messages';
import { MutationConfig } from './AddBeneficiaryPopup.types';

export const useFieldsSchema = (payload: backend.transfers.AirwallexBeneficiarySchemaTransfer) => {
    return useQuery(
        [QueryKeys.AIRWALLEX_GET_BENEFICIARY_SCHEMA, payload],
        async () => await api.airwallex.getBeneficiarySchema(payload),
        {
            select: tuneAirwallexFieldsAnswer,
            cacheTime: 0,
        }
    );
};

export const useCreateBeneficiaryMutation = (config: MutationConfig) => {
    const { companyId, requestId, initialBillId, setIsOpenSuccessPopup, setErrorMessages, setIsOpen2FA, fieldsSchema } =
        config;
    const dispatch = useDispatch();
    const { changeBeneficiary } = useXeroAirwallexBatchPaymentFormContext();

    return useMutation(
        async ({
            oneTimePassword,
            beneficiaryFields,
        }: Pick<backend.transfers.AirwallexCreateBeneficiaryTransfer, 'oneTimePassword' | 'beneficiaryFields'>) =>
            api.airwallex.createBeneficiary({
                companyId,
                requestId,
                oneTimePassword,
                beneficiaryFields,
            }),
        {
            onSuccess: (response) => {
                setIsOpenSuccessPopup(true);
                setIsOpen2FA(false);
                dispatch(clearCacheStorage((storageId) => String(storageId).startsWith('requestOptions')));

                if (initialBillId && response.Beneficiary)
                    changeBeneficiary(initialBillId, {
                        ...response.Beneficiary,
                        bankDetails: {
                            ...response.Beneficiary.bankDetails,

                            accountingRoutingType1:
                                response.Beneficiary.bankDetails.accountingRoutingType1 ||
                                response.Beneficiary.bankDetails.accountRoutingType1 ||
                                '',
                            accountingRoutingType2:
                                response.Beneficiary.bankDetails.accountingRoutingType2 ||
                                response.Beneficiary.bankDetails.accountRoutingType2 ||
                                '',
                            accountingRoutingValue1:
                                response.Beneficiary.bankDetails.accountingRoutingValue1 ||
                                response.Beneficiary.bankDetails.accountRoutingValue1 ||
                                '',
                            accountingRoutingValue2:
                                response.Beneficiary.bankDetails.accountingRoutingValue2 ||
                                response.Beneficiary.bankDetails.accountRoutingValue2 ||
                                '',
                        },
                        text: response.Beneficiary.name,
                        paymentFee: null,
                        paymentMethod: response.Beneficiary.paymentMethod
                            ? { id: response.Beneficiary.paymentMethod, text: response.Beneficiary.paymentMethod }
                            : null,
                        id: response.Beneficiary.airwallexBeneficiaryId,
                    });
            },
            onError: (e: ApiError) => {
                if (e.code === ErrorCode.E4002_WRONG_CONFIRMATION_CODE) {
                    setErrorMessages([e.title || messages.unknownError]);
                } else if (e.code === ErrorCode.E7711_BENEFICIARY_CREATION_VALIDATION_ERROR && e.errors?.length) {
                    const errorMessages = e.errors.map((error) => `${error.path}: ${error.message}`);

                    setErrorMessages(errorMessages);
                } else {
                    let errorText: string = messages.unknownError;
                    let parameterName = '';

                    if (e.parameterName && fieldsSchema) {
                        const field = fieldsSchema.find((field) => field.path === e.parameterName);

                        if (field) {
                            parameterName = field.field.label;
                        }
                    }

                    if (e.detail && parameterName) {
                        errorText = `${e.detail} (${parameterName})`;
                    } else if (e.detail) {
                        errorText = e.detail;
                    }

                    setErrorMessages([errorText]);
                }

                setIsOpen2FA(false);
            },
            onMutate: () => {
                setIsOpen2FA(false);
            },
        }
    );
};

export const useIsInitialCurrencySupported = ({
    activeStep,
    prevFieldsSchema,
    fieldsSchema,
    closePopup,
    initialCurrency,
}: {
    activeStep: number;
    prevFieldsSchema?: backend.AirwallexBenefeciarySchemaField[];
    fieldsSchema?: backend.AirwallexBenefeciarySchemaField[];
    closePopup: () => void;
    initialCurrency?: string;
}) => {
    if (activeStep === 1 && !prevFieldsSchema && fieldsSchema) {
        const fieldCurrency = fieldsSchema.find((field) => field.path === accountCurrencyKey);

        if (fieldCurrency) {
            const isCurrencySupported = Boolean(
                fieldCurrency.field.options?.find((option) => option.value === initialCurrency)
            );

            if (!isCurrencySupported) {
                notificationService.showErrorToast(messages.notSupportedCurrency({ currency: initialCurrency }));
                closePopup();
            }
        }
    }
};

export const usePrepopulateFormValuesFromDefaultValues = ({
    fieldsSchema,
    fieldsTouched,
    formValues,
    prevFieldsSchema,
    setFormValues,
    initialCurrency,
    integrationCountry,
}: {
    fieldsSchema?: backend.AirwallexBenefeciarySchemaField[];
    fieldsTouched: Record<string, boolean>;
    formValues: Record<string, string>;
    prevFieldsSchema?: backend.AirwallexBenefeciarySchemaField[];
    setFormValues: Dispatch<SetStateAction<Record<string, string>>>;
    initialCurrency?: string;
    integrationCountry?: string;
}) => {
    if (!fieldsSchema || prevFieldsSchema === fieldsSchema || isEqual(prevFieldsSchema, fieldsSchema)) {
        return;
    }

    const defaultFormValues = fieldsSchema.reduce<Record<string, string>>((acc, currentField) => {
        acc[currentField.path] = currentField.field.default;

        return acc;
    }, {});

    const newFormValues: Record<string, string> = {};

    for (const key in defaultFormValues) {
        if (!formValues[key]) {
            newFormValues[key] = defaultFormValues[key];
        } else if (fieldsTouched[key] && formValues[key]) {
            const field = fieldsSchema.find((field) => field.path === key);

            if (!field) {
                continue;
            }

            if (field.field.options) {
                const selectedOption = field.field.options.find((option) => option.value === formValues[key]);

                newFormValues[key] = selectedOption ? formValues[key] : defaultFormValues[key];
            } else {
                newFormValues[key] = formValues[key];
            }
        } else {
            newFormValues[key] = defaultFormValues[key];
        }
    }

    newFormValues[accountCurrencyKey] = formValues[accountCurrencyKey] || initialCurrency || '';

    /**
     * set country when just opened popup by mapping country codes from Xero to AWX or use default AWX value for other
     * countries
     */
    if (!prevFieldsSchema && fieldsSchema && !formValues[bankCountryCodeKey]) {
        switch (integrationCountry) {
            case 'UnitedKingdom':
                newFormValues[bankCountryCodeKey] = 'GB';
                break;

            case 'UnitedStates':
                newFormValues[bankCountryCodeKey] = 'US';
                break;

            case 'Australia':
                newFormValues[bankCountryCodeKey] = 'AU';
                break;

            case 'NewZealand':
                newFormValues[bankCountryCodeKey] = 'NZ';
                break;
        }
    }

    setFormValues({ ...formValues, ...newFormValues });
};

export const useReloadSchemaIfLocalClearingFieldIsRequired = ({
    activeStep,
    fieldsSchema,
    formValues,
    prevFormValues,
    companyId,
    setFieldsSchemaTransfer,
    fieldsSchemaTransfer,
}: {
    activeStep: number;
    fieldsSchema?: backend.AirwallexBenefeciarySchemaField[];
    formValues: Record<string, string>;
    prevFormValues?: Record<string, string>;
    companyId: string;
    setFieldsSchemaTransfer: Dispatch<SetStateAction<backend.transfers.AirwallexBeneficiarySchemaTransfer>>;
    fieldsSchemaTransfer: backend.transfers.AirwallexBeneficiarySchemaTransfer;
}) => {
    if (activeStep !== 2) return;

    const onlyPaymentMethodChanged =
        prevFormValues &&
        formValues !== prevFormValues &&
        formValues[paymentMethodKey] !== prevFormValues[paymentMethodKey] &&
        formValues[entityTypeKey] === prevFormValues[entityTypeKey];

    const missLocalClearingField =
        fieldsSchema &&
        formValues[paymentMethodKey] === 'LOCAL' &&
        !fieldsSchema.find((field) => field.path === localClearingSystemKey);

    if (onlyPaymentMethodChanged || missLocalClearingField) {
        const newFieldsSchemaTransfer = {
            companyId: companyId,
            accountCurrency: formValues[accountCurrencyKey],
            bankCountryCode: formValues[bankCountryCodeKey],
            entityType: formValues[entityTypeKey],
            paymentMethod: formValues[paymentMethodKey],
        };

        if (!isEqual(newFieldsSchemaTransfer, fieldsSchemaTransfer)) {
            setFieldsSchemaTransfer({
                companyId: companyId,
                accountCurrency: formValues[accountCurrencyKey],
                bankCountryCode: formValues[bankCountryCodeKey],
                entityType: formValues[entityTypeKey],
                paymentMethod: formValues[paymentMethodKey],
            });
        }
    }
};

export const useCheckAvailablePaymentMethods = ({
    activeStep,
    fieldsSchema,
    prevFieldsSchema,
    setFieldsSchemaTransfer,
    setActiveStep,
}: {
    activeStep: number;
    fieldsSchema?: backend.AirwallexBenefeciarySchemaField[];
    prevFieldsSchema?: backend.AirwallexBenefeciarySchemaField[];
    setFieldsSchemaTransfer: Dispatch<SetStateAction<backend.transfers.AirwallexBeneficiarySchemaTransfer>>;
    setActiveStep: Dispatch<SetStateAction<number>>;
}) => {
    if (!fieldsSchema || activeStep !== 2 || prevFieldsSchema === fieldsSchema) {
        return;
    }

    const paymentMethodField = fieldsSchema.find((field) => field.path === paymentMethodKey);
    const paymentMethodOptions = paymentMethodField?.field.options;

    if (paymentMethodOptions && !paymentMethodOptions.length) {
        notificationService.showErrorToast(messages.notSupported);
        setFieldsSchemaTransfer((prevFieldsSchemaTransfer) => ({
            ...prevFieldsSchemaTransfer,
            bankCountryCode: undefined,
            accountCurrency: undefined,
        }));
        setActiveStep(1);
    }
};

export const useKeepValidationErrorsUpdated = ({
    fieldsSchema,
    prevFieldsSchema,
    formValues,
    activeStep,
    prevActiveStep,
    validationErrors,
    setValidationErrors,
}: {
    fieldsSchema?: backend.AirwallexBenefeciarySchemaField[];
    prevFieldsSchema?: backend.AirwallexBenefeciarySchemaField[];
    formValues: Record<string, string>;
    activeStep: number;
    prevActiveStep?: number;
    validationErrors?: Record<string, boolean>;
    setValidationErrors?: Dispatch<SetStateAction<Record<string, boolean>>>;
}) => {
    if (
        activeStep === 3 &&
        validationErrors &&
        setValidationErrors &&
        fieldsSchema &&
        (prevActiveStep !== 3 || !prevFieldsSchema)
    ) {
        const newValidationErrors: typeof validationErrors = {};

        fieldsSchema.forEach((field) => {
            const isFieldForStepOne = keysGeneral.includes(field.path);
            const isFieldForStepTwo = keysPaymentMethod.includes(field.path);

            if (isFieldForStepOne || isFieldForStepTwo) {
                return;
            }

            const validationRule = field.rule?.pattern;

            if (validationRule && formValues[field.path] && field.enabled) {
                const isValidValue = new RegExp(validationRule).test(formValues[field.path]);

                if (!isValidValue) {
                    newValidationErrors[field.path] = true;
                }
            }
        });

        setValidationErrors(newValidationErrors);
    }
};

export const useNestedPopups = () => {
    const [isOpen2FA, setIsOpen2FA] = useState(false);
    const [isOpenSuccessPopup, setIsOpenSuccessPopup] = useState(false);
    const [isUnsavedChangesPopupOpen, setIsUnsavedChangesPopupOpen] = useState(false);

    const close2FAPopup = useCallback(() => setIsOpen2FA(false), []);
    const closeUnsavedChangesPopup = useCallback(() => setIsUnsavedChangesPopupOpen(false), []);
    const openUnsavedChangesPopup = useCallback(() => setIsUnsavedChangesPopupOpen(true), []);

    return {
        isOpen2FA,
        setIsOpen2FA,
        close2FAPopup,
        isOpenSuccessPopup,
        setIsOpenSuccessPopup,
        isUnsavedChangesPopupOpen,
        closeUnsavedChangesPopup,
        openUnsavedChangesPopup,
    };
};
